import React, { useState, useEffect, useRef } from 'react';
import { X, CheckCircle, AlertTriangle, ChevronDown } from 'lucide-react';

// --- BUTTON ---
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'success' | 'outline' | 'warning';
  size?: 'sm' | 'md' | 'lg';
}

export const Button: React.FC<ButtonProps> = ({ variant = 'primary', size = 'md', className = '', children, ...props }) => {
  const baseStyle = "inline-flex items-center justify-center rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none";
  
  const variants = {
    primary: "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500",
    secondary: "bg-slate-100 text-slate-900 hover:bg-slate-200 focus:ring-slate-500",
    danger: "bg-red-500 text-white hover:bg-red-600 focus:ring-red-500",
    success: "bg-emerald-500 text-white hover:bg-emerald-600 focus:ring-emerald-500",
    warning: "bg-amber-500 text-white hover:bg-amber-600 focus:ring-amber-500",
    outline: "border border-slate-300 bg-transparent hover:bg-slate-50 text-slate-700"
  };

  const sizes = {
    sm: "h-8 px-3 text-xs",
    md: "h-10 px-4 py-2 text-sm",
    lg: "h-12 px-6 text-base"
  };

  return (
    <button className={`${baseStyle} ${variants[variant]} ${sizes[size]} ${className}`} {...props}>
      {children}
    </button>
  );
};

// --- CARD ---
export const Card: React.FC<{ title?: string; children: React.ReactNode; className?: string }> = ({ title, children, className = '' }) => (
  <div className={`bg-white rounded-lg border border-slate-200 shadow-sm ${className}`}>
    {title && <div className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-800">{title}</div>}
    <div className="p-4">{children}</div>
  </div>
);

// --- INPUT ---
interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
}
export const Input: React.FC<InputProps> = ({ label, className = '', ...props }) => (
  <div className="space-y-1">
    {label && <label className="block text-sm font-medium text-slate-700">{label}</label>}
    <input 
      className={`w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${className}`}
      {...props} 
    />
  </div>
);

// --- SELECT ---
interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  options: { value: string | number; label: string }[];
}
export const Select: React.FC<SelectProps> = ({ label, options, className = '', ...props }) => (
  <div className="space-y-1">
    {label && <label className="block text-sm font-medium text-slate-700">{label}</label>}
    <select 
      className={`w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${className}`}
      {...props}
    >
      {options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
    </select>
  </div>
);

// --- SEARCH SELECT: выбор из длинного списка с поиском с клавиатуры ---
// Список рисуется position: fixed — не обрезается прокруткой модального окна и таблиц.
const normSearch = (s: string) => s.toLowerCase().replace(/ё/g, 'е');
interface SearchSelectProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}
export const SearchSelect: React.FC<SearchSelectProps> = ({ label, value, onChange, options, placeholder = 'Начните вводить…', className = '', disabled }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const selected = options.find(o => o.value === value);
  const words = normSearch(query).split(/\s+/).filter(Boolean);
  const shown = options.filter(o => words.every(w => normSearch(o.label).includes(w)));

  useEffect(() => {
    if (!open) return;
    const place = () => inputRef.current && setRect(inputRef.current.getBoundingClientRect());
    const outside = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!inputRef.current?.contains(t) && !listRef.current?.contains(t)) setOpen(false);
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    document.addEventListener('mousedown', outside);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); document.removeEventListener('mousedown', outside); };
  }, [open]);
  useEffect(() => { listRef.current?.children[active]?.scrollIntoView({ block: 'nearest' }); }, [active]);

  const pick = (v: string) => { onChange(v); setOpen(false); setQuery(''); inputRef.current?.blur(); };
  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive(a => Math.min(a + 1, shown.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (open && shown[active]) pick(shown[active].value); }
    else if (e.key === 'Escape') { if (open) { e.stopPropagation(); setOpen(false); setQuery(''); } }
  };
  const below = !rect || window.innerHeight - rect.bottom > 260 || rect.top < 260;

  return (
    <div className="space-y-1">
      {label && <label className="block text-sm font-medium text-slate-700">{label}</label>}
      <div className="relative">
        <input
          ref={inputRef}
          disabled={disabled}
          className={`w-full rounded-md border border-slate-300 pl-3 pr-8 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 ${className}`}
          value={open ? query : selected?.label || ''}
          placeholder={open ? selected?.label || placeholder : placeholder}
          onFocus={() => { setOpen(true); setQuery(''); setActive(Math.max(0, options.findIndex(o => o.value === value))); }}
          onChange={e => { setQuery(e.target.value); setActive(0); setOpen(true); }}
          onKeyDown={onKey}
          onBlur={() => { setOpen(false); setQuery(''); }}
        />
        <ChevronDown size={14} className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
      </div>
      {open && rect && (
        <div ref={listRef} onMouseDown={e => e.preventDefault()}
          style={{ position: 'fixed', left: rect.left, width: Math.max(rect.width, 240), ...(below ? { top: rect.bottom + 4 } : { bottom: window.innerHeight - rect.top + 4 }) }}
          className="z-[60] max-h-60 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg">
          {shown.map((o, i) => (
            <div key={o.value} onClick={() => pick(o.value)} onMouseEnter={() => setActive(i)}
              className={`cursor-pointer px-3 py-2 text-sm ${i === active ? 'bg-blue-50 text-blue-800' : 'text-slate-700'} ${o.value === value ? 'font-medium' : ''}`}>
              {o.label}
            </div>
          ))}
          {!shown.length && <div className="px-3 py-2 text-sm text-slate-400">Ничего не найдено</div>}
        </div>
      )}
    </div>
  );
};

// --- MULTI SELECT (ВОТ ЕГО НЕ ХВАТАЛО) ---
interface MultiSelectProps {
  label?: string;
  options: { value: string; label: string }[];
  value: string[];
  onChange: (selected: string[]) => void;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({ label, options, value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (optValue: string) => {
    if (value.includes(optValue)) {
      onChange(value.filter(v => v !== optValue));
    } else {
      onChange([...value, optValue]);
    }
  };

  return (
    <div className="relative space-y-1" ref={containerRef}>
      {label && <label className="block text-sm font-medium text-slate-700">{label}</label>}
      <div
        className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white cursor-pointer min-h-[38px] flex flex-wrap gap-1 items-center justify-between"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex flex-wrap gap-1">
            {value.length === 0 && <span className="text-slate-400">Все...</span>}
            {value.map(v => {
            const opt = options.find(o => o.value === v);
            return (
                <span key={v} className="bg-blue-50 text-blue-700 border border-blue-100 text-xs px-1.5 py-0.5 rounded flex items-center">
                {opt?.label || v}
                </span>
            );
            })}
        </div>
        <ChevronDown size={14} className="text-slate-400" />
      </div>
      
      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-auto">
          {options.map(opt => (
            <div
              key={opt.value}
              className="px-3 py-2 hover:bg-slate-50 flex items-center gap-2 cursor-pointer border-b border-slate-50 last:border-0"
              onClick={() => toggleOption(opt.value)}
            >
              <input
                type="checkbox"
                checked={value.includes(opt.value)}
                readOnly
                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-slate-700">{opt.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// --- BADGE ---
export const Badge: React.FC<{ variant?: 'success' | 'warning' | 'danger' | 'neutral'; children: React.ReactNode }> = ({ variant = 'neutral', children }) => {
  const styles = {
    success: "bg-emerald-100 text-emerald-800",
    warning: "bg-amber-100 text-amber-800",
    danger: "bg-red-100 text-red-800",
    neutral: "bg-slate-100 text-slate-800"
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${styles[variant]}`}>
      {children}
    </span>
  );
};

// --- MODAL ---
export const Modal: React.FC<{ isOpen: boolean; onClose: () => void; title: string; children: React.ReactNode; wide?: boolean; xl?: boolean }> = ({ isOpen, onClose, title, children, wide, xl }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      {/* На телефоне — не шире экрана минус отступы, прокрутка внутри окна */}
      <div className={`w-full max-w-[calc(100vw-2rem)] ${xl ? 'sm:max-w-6xl' : wide ? 'sm:max-w-3xl' : 'sm:max-w-lg'} max-h-[90vh] flex flex-col rounded-xl bg-white shadow-2xl ring-1 ring-black/5`}>
        <div className="flex items-center justify-between gap-2 border-b p-3 sm:p-4">
          <h3 className="min-w-0 break-words text-base sm:text-lg font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} className="shrink-0 text-slate-400 hover:text-slate-600"><X size={20} /></button>
        </div>
        <div className="p-3 sm:p-4 min-h-0 flex-1 overflow-y-auto overflow-x-hidden">{children}</div>
      </div>
    </div>
  );
};

// --- TOAST (УВЕДОМЛЕНИЯ) ---
export const Toast: React.FC<{ message: string; type: 'success' | 'error'; onClose: () => void }> = ({ message, type, onClose }) => {
  const isError = type === 'error';
  
  return (
    // Контейнер на весь экран для центрирования
    <div className="fixed inset-0 z-[100] flex items-center justify-center pointer-events-none">
      {/* Само окно сообщения */}
      <div className={`pointer-events-auto flex flex-col items-center gap-3 px-6 sm:px-8 py-6 rounded-2xl shadow-2xl border transform transition-all animate-bounce-in bg-white w-[calc(100vw-2rem)] sm:w-auto sm:min-w-[300px] max-w-md
        ${isError ? 'border-red-100' : 'border-emerald-100'}
      `}>
        {/* Иконка */}
        <div className={`p-3 rounded-full ${isError ? 'bg-red-50 text-red-500' : 'bg-emerald-50 text-emerald-500'}`}>
          {isError ? <AlertTriangle size={32} /> : <CheckCircle size={32} />}
        </div>
        
        {/* Текст */}
        <div className="text-center">
           <h3 className={`font-bold text-lg mb-1 ${isError ? 'text-red-900' : 'text-emerald-900'}`}>
             {isError ? 'Внимание' : 'Готово'}
           </h3>
           <p className="text-slate-600 text-sm">{message}</p>
        </div>

        {/* Кнопка закрытия (опционально, так как оно само исчезнет, но для стиля оставим) */}
        <button 
          onClick={onClose}
          className={`mt-2 px-6 py-2 rounded-lg text-sm font-medium transition-colors ${
            isError 
            ? 'bg-red-50 text-red-700 hover:bg-red-100' 
            : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
          }`}
        >
          OK
        </button>
      </div>
    </div>
  );
};